import { html, css, nothing, PropertyValues } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import NSJobComponent from '../../job.js';

import JobSchema from '../../../../models/job-schema.js';
import Person from '../../../../models/person.js';
import Company from '../../../../models/company.js';
import { LiveAnnouncer } from '../../../../helpers/live-announcer.js';
import { updatePersonClaims } from '../../../../helpers/lambda.js';
import { nsPersonRowStyles } from '../../../../ns-styles/index.js';

import type { Option } from '../../../../ns-elements/ns-multi-select.js';
import '../../../../ns-elements/ns-multi-select.js';
import '../../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import type WaButton from '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import type WaCheckbox from '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import definition from '@job-schemas/company/reporting-to.json';
import { jobSchema } from '../../job-schema.js';

interface SelectedManager {
  id: string;
  name: string;
  email: string;
  role?: string;
  avatar?: string;
  primary: boolean;
}

@customElement(NSReportingTo.is)
export default class NSReportingTo extends NSJobComponent {
  static get is() {
    return 'ns-reporting-to';
  }

  @query('.select-button')
  private __selectButton?: WaButton;

  // The <ns-multi-select> host itself -- used both to reach its internal native input for
  // disabled/focus management (it has no `disabled` prop and we don't modify shared components; see
  // `updated()` and `__focusSearchInput()` below) and as a stable focus target after list changes.
  @query('#managers-search')
  private __searchInputHost?: HTMLElement;

  private __announcer = new LiveAnnouncer(this);

  @state()
  private __people?: Person[];

  @state()
  private __company?: Company;

  // Whether the person has told us they don't report to anyone within this company. Mutually
  // exclusive with the search/select flow below -- ticking this disables the search input and
  // replaces the managers list with explanatory copy instead.
  @state()
  private __doesNotReportToAnyone = false;

  // Candidate people available to search for: everyone at the company minus the current user and
  // minus anyone already committed to `__selectedManagers`. Deliberately NOT filtered against
  // `__pendingSelected` -- ns-multi-select shows/toggles already-picked-but-not-yet-committed
  // options itself (see its `.check` indicator), matching how it's used elsewhere (e.g.
  // connect/request.ts).
  @state()
  private __allCandidates: Option[] = [];

  // `__allCandidates` filtered down by whatever's currently typed into the search box.
  @state()
  private __searchResults: Option[] = [];

  @state()
  private __searchStatus = '';

  private __searchQuery = '';

  // Chips staged inside the search input itself, picked from the dropdown but not yet committed
  // to the managers list below -- committed by clicking "Select".
  @state()
  private __pendingSelected: Option[] = [];

  // The committed, persisted list of managers shown in the fixed-height list, exactly one of
  // which (when non-empty) is flagged `primary`.
  @state()
  private __selectedManagers: SelectedManager[] = [];

  @state()
  private __brokenAvatars = new Set<string>();

  private __searchAnnounceTimer?: number;

  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: { complete: { type: 'button', label: 'Complete' } },
      icon: 'person',
    });
  }

  private get __companyName() {
    return this.__company?.name ?? 'your company';
  }

  private __announce(message: string) {
    window.clearTimeout(this.__searchAnnounceTimer);
    this.__announcer.announce(message);
  }

  private __getInitials(name: string): string {
    if (!name) return '';
    return name
      .split(' ')
      .filter((part) => part)
      .map((part) => part[0]?.toUpperCase())
      .join('');
  }

  private __onAvatarError(key: string) {
    this.__brokenAvatars = new Set(this.__brokenAvatars).add(key);
  }

  static styles = [
    ...super.styles,
    nsPersonRowStyles,
    css`
      wa-checkbox {
        font-size: var(--wa-font-size-m);
      }
      .company-name {
        color: var(--ns-theme-purple);
        font-weight: 600;
      }

      .search-field {
        gap: var(--wa-space-xs);
      }

      .search-field label {
        font-size: var(--wa-font-size-m);
        font-weight: 600;
        color: var(--primary-text-color);
      }

      .search-row {
        gap: var(--wa-space-xs);
      }

      .search-input-wrapper {
        min-width: 0;
      }

      .search-row > .search-input-wrapper[aria-disabled='true'] {
        opacity: 0.5;
        pointer-events: none;
      }

      /* The shared person row (nsPersonRowStyles), with a primary-manager radio before the avatar */
      .person-row {
        grid-template-columns: auto auto minmax(0, 1.2fr) minmax(0, 1.4fr) minmax(0, 1.2fr) auto;
      }

      .person-row__primary {
        grid-column: 1 / span 5;
        display: grid;
        grid-template-columns: subgrid;
        align-items: center;
        cursor: pointer;

        font-size: inherit;
        font-weight: inherit;
        line-height: inherit;
      }

      .person-row__primary input[type='radio'] {
        appearance: none;
        width: 15px;
        height: 15px;
        margin: 0;
        padding: 0;

        border-radius: 50%;
        background: var(--ns-theme-grey-dark);
        box-shadow:
          0 0 0 1px var(--ns-theme-white),
          0 0 0 2px var(--ns-theme-type);

        cursor: pointer;
      }

      .person-row__primary input[type='radio']:checked {
        background: var(--ns-theme-green);
      }

      .person-row__primary input[type='radio']:focus-visible {
        outline: 2px solid var(--ns-theme-purple);
        outline-offset: 2px;
        box-shadow:
          0 0 0 1px var(--ns-theme-white),
          0 0 0 3px var(--ns-theme-type);
      }

      .empty-state {
        max-width: 320px;
        margin: 0 auto;
        padding: var(--wa-space-l) var(--wa-space-m);
        text-align: center;
      }

      .empty-state__title {
        color: var(--primary-card-text-color);
        font-weight: 600;
        margin-bottom: var(--wa-space-xs);
      }

      .empty-state__body {
        color: var(--primary-card-text-color);
        opacity: 0.75;
        font-size: 0.9em;
        line-height: 1.4;
      }

      @media (forced-colors: active) {
        .person-row__primary input[type='radio'] {
          border: 1px solid CanvasText;
        }
        .person-row__primary input[type='radio']:checked {
          forced-color-adjust: none;
          background: Highlight;
        }
      }
    `,
  ];

  async connectedCallback() {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    try {
      await super.connectedCallback();

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._directory) throw new Error('Unable to find NSDirectoryService');

      this.__company = await this.loadJobCompany();
      const companyId = this.__company.id;

      const doesNotReportToAnyone = this.getMetadataValue('DOES_NOT_REPORT_TO_ANYONE');
      if (doesNotReportToAnyone) this.__doesNotReportToAnyone = doesNotReportToAnyone === 'true';

      const selectedManagersMetadata = this.getMetadataValue('SELECTED_MANAGERS');
      if (selectedManagersMetadata) {
        // Normalise against older metadata shapes that predate the `primary` field.
        const parsed = JSON.parse(selectedManagersMetadata) as Partial<SelectedManager>[];
        this.__selectedManagers = parsed.map((manager) => ({
          id: manager.id as string,
          name: manager.name as string,
          email: manager.email as string,
          role: manager.role,
          avatar: manager.avatar,
          primary: !!manager.primary,
        }));
        this.__ensureSinglePrimary();
      } else {
        // No metadata on THIS job instance yet -- e.g. it's been re-triggered as a correction for
        // someone who already has managers recorded from an earlier submission of this same job.
        // Seed the UI from what's actually persisted so Complete's reconciliation (see
        // `__reconcileReportsTo`) diffs against what the user is shown, instead of starting from an
        // empty list and silently deleting their existing managers the moment this runs again.
        const existingReportsTo = this._directory.me?.reportsTo;
        if (existingReportsTo && existingReportsTo.length > 0) {
          this.__selectedManagers = existingReportsTo.map((r) => ({
            id: r.id,
            name: r.email, // placeholder -- __updateList resolves the real name once people load
            email: r.email,
            primary: r.primary,
          }));
          this.__ensureSinglePrimary();
        }
      }

      await this.__loadPeople(companyId);

      this._subscriptions.subscribe('people.*', () => this.__loadPeople(companyId));
    } catch (err: unknown) {
      this.showError(err);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.__searchAnnounceTimer);
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    // ns-multi-select has no `disabled` prop, and it's a shared component we've been told not to
    // modify. Rather than hide the whole control from assistive tech with `inert` (which strips it
    // from the accessibility tree entirely -- a screen-reader user browsing the form wouldn't even
    // know a search field exists there), toggle native `disabled`/tabindex directly on its rendered
    // internal input, so it's discoverable-but-inert like an ordinary disabled form control.
    const input = this.__searchInputHost?.shadowRoot?.getElementById('search-input') as HTMLInputElement | null;
    if (input) {
      input.disabled = this.__doesNotReportToAnyone;
      input.tabIndex = this.__doesNotReportToAnyone ? -1 : 0;
    }
  }

  private async __loadPeople(companyId: string) {
    if (!this._db) return;

    const peopleQueryCall = await this._db.query('people', {
      companyId: {
        $eq: companyId,
      },
    });
    this.__people = peopleQueryCall.results as unknown as Person[];

    this.__updateList();
  }

  async _outcomeCompleteHandler() {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    this.clearError();

    try {
      if (!this._db) throw new Error('Missing required db service');
      if (!this._directory) throw new Error('Missing directory service');
      if (!this.__company || !this.__company.id) throw new Error('Missing company');
      if (!this.__selectedManagers) throw new Error('Missing required selected managers');

      const profile = this._directory.me;
      if (!profile || !profile!.id) throw new Error('Missing required person ID');

      if (!this.__doesNotReportToAnyone) {
        this.__ensureSinglePrimary();
        if (this.__selectedManagers.length === 0) {
          throw new Error(
            'Please select at least one manager, or tick "I do not report to anyone within this company".',
          );
        }
        if (!this.__selectedManagers.some((sm) => sm.primary)) {
          throw new Error('Please choose which of your selected managers is your primary manager.');
        }
      }

      this.loading('Updating Managers List');

      await this.__reconcileReportsTo(profile);

      this.loaded();
      this.__announce('Managers list updated.');
      this.outcome('complete');
    } catch (err: unknown) {
      this.loaded();
      this.showError(err);
    }
  }

  private async __reconcileReportsTo(profile: Person) {
    const reportsTo = this.__doesNotReportToAnyone
      ? []
      : this.__selectedManagers.map(({ id, primary }) => ({ id, primary }));
    await updatePersonClaims(this._auth?.token, profile.id, { reportsTo });
  }

  private __updateList() {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    try {
      if (!this.__people) throw new Error('Missing required company employee');
      if (!this.__selectedManagers) throw new Error('Missing required selected managers to update list');

      // Keep committed managers' display details in sync with the roster -- this is also what turns
      // the resume-seeded entries from `connectedCallback` (which only start out with the email as a
      // name placeholder) into proper names once `__people` has loaded.
      this.__selectedManagers = this.__selectedManagers.map((manager) => {
        const person = this.__people?.find((i) => i.id === manager.id);
        if (!person) return manager;

        let name = person.forename ? person.forename : '';
        name = person.surname ? `${name} ${person.surname}` : name;
        name = (name as string) || (person.email as string) || manager.name;

        return {
          ...manager,
          name: name as string,
          email: (person.email as string) ?? manager.email,
          role: (person.jobTitle as string) ?? manager.role,
          avatar: 'avatar' in person ? (person.avatar ?? undefined) : manager.avatar,
        };
      });

      this.__allCandidates = this.__people.reduce((arr: Option[], i) => {
        if (this._directory?.me?.email === i.email) return arr;
        if (this.__selectedManagers.findIndex((sm) => sm.id === i.id) !== -1) return arr;

        let name = i.forename ? i.forename : '';
        name = i.surname ? `${name} ${i.surname}` : name;

        arr.push({
          value: i.id,
          label: ((name as string) || i.email || 'Unnamed') as string,
        });
        return arr;
      }, []);

      this.__filterCandidates();
    } catch (err: unknown) {
      this.showError(err);
    }
  }

  private __filterCandidates() {
    const query = this.__searchQuery.trim().toLowerCase();
    this.__searchResults = !query
      ? this.__allCandidates
      : this.__allCandidates.filter((option) => option.label.toLowerCase().includes(query));
    this.__searchStatus = query && this.__searchResults.length === 0 ? "We couldn't find anyone matching that." : '';
  }

  private __onSearchInput(evt: CustomEvent<{ query: string }>) {
    this.__searchQuery = evt.detail.query;
    this.__filterCandidates();

    // Announce after typing pauses so screen readers aren't interrupted on every keystroke.
    window.clearTimeout(this.__searchAnnounceTimer);
    this.__searchAnnounceTimer = window.setTimeout(() => {
      if (this.__searchStatus) this.__announce(this.__searchStatus);
    }, 400);
  }

  private __onSelectionChanged(evt: CustomEvent<{ selected: Option[] }>) {
    this.__pendingSelected = evt.detail.selected;
  }

  // Keeps `__selectedManagers` honest as "at most one, and at least one when non-empty, `primary:
  // true`" -- called after every mutation of the array instead of duplicating that logic ad hoc at
  // each call site.
  private __ensureSinglePrimary() {
    const primaries = this.__selectedManagers.filter((sm) => sm.primary);

    if (primaries.length === 0) {
      if (this.__selectedManagers.length > 0) this.__selectedManagers[0].primary = true;
      return;
    }

    if (primaries.length > 1) {
      // Guards against malformed input -- hand-edited job metadata, an older version of this
      // component, or (in principle) a resume-seed from `profile.reportsTo` that already had more
      // than one primary -- by keeping only the first and demoting the rest.
      let kept = false;
      this.__selectedManagers.forEach((sm) => {
        if (!sm.primary) return;
        if (kept) {
          sm.primary = false;
          return;
        }
        kept = true;
      });
    }
  }

  private __focusSearchInput() {
    const input = this.__searchInputHost?.shadowRoot?.getElementById('search-input') as HTMLElement | null;
    input?.focus();
  }

  // Restores focus after a Select commit or a list removal. The select button is frequently
  // disabled by the time this runs (pendingSelected is empty again), and calling `.focus()` on a
  // disabled native button is a documented no-op -- silently dropping focus to <body>. Prefer, in
  // order: the select button (if it's still enabled), the next remaining manager row's remove
  // button, then the search input.
  private __focusAfterListChange() {
    this.updateComplete.then(() => {
      if (this.__selectButton && !this.__selectButton.disabled) {
        this.__selectButton.focus();
        return;
      }

      const firstRemaining = this.renderRoot.querySelector<HTMLElement>('.person-row__action');
      if (firstRemaining) {
        firstRemaining.focus();
        return;
      }

      this.__focusSearchInput();
    });
  }

  private __onSelect() {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    this.clearError();
    window.clearTimeout(this.__searchAnnounceTimer);

    try {
      if (!this.__people) throw new Error('Missing required company employee');
      if (this.__pendingSelected.length === 0)
        throw new Error('Please choose at least one person from the search results first.');

      const addedNames: string[] = [];
      const missingNames: string[] = [];

      this.__pendingSelected.forEach((option) => {
        const person = this.__people?.find((i) => i.id === option.value);
        if (!person) {
          // The roster refreshed (realtime `people.*` subscription) while this person was staged as
          // a chip, and they're no longer there -- don't silently drop them, surface it below.
          missingNames.push(option.label);
          return;
        }

        let name = person.forename ? person.forename : '';
        name = person.surname ? `${name} ${person.surname}` : name;
        name = (name as string) || person.email || 'Unnamed';

        this.__selectedManagers.push({
          id: person.id,
          name: name as string,
          email: person.email as string,
          role: person.jobTitle as string,
          avatar: person.avatar as string,
          primary: false,
        });
        addedNames.push(name as string);
      });

      this.__pendingSelected = [];
      this.__ensureSinglePrimary();
      this.__updateList();

      this.updateMetadata('SELECTED_MANAGERS', JSON.stringify(this.__selectedManagers));
      this.requestUpdate('__selectedManagers');

      if (missingNames.length > 0) {
        const missingMessage = `${missingNames.join(', ')} ${
          missingNames.length === 1 ? 'is' : 'are'
        } no longer available and ${missingNames.length === 1 ? "wasn't" : "weren't"} added.`;
        // The card error is visible and role="alert", so it's announced without __announce too.
        this.showError(new Error(missingMessage));
      }
      if (addedNames.length > 0) {
        this.__announce(`${addedNames.join(', ')} added to your managers list.`);
      }

      this.updateComplete.then(() => this.__focusSearchInput());
    } catch (err: unknown) {
      this.showError(err);
      this.__focusAfterListChange();
    }
  }

  private __deselectManager(id: string) {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    this.clearError();

    try {
      if (!this.__selectedManagers) throw new Error('Missing required selectedManagers list');

      const idx = this.__selectedManagers.findIndex((sm) => sm.id === id);
      if (idx === -1) throw new Error(`Could not find a manager with ID: ${id} in the selected managers list`);
      const manager = this.__selectedManagers[idx];
      this.__selectedManagers.splice(idx, 1);
      this.__ensureSinglePrimary();

      this.__announce(`${manager.name} removed from your managers list.`);

      this.__updateList();
      this.updateMetadata('SELECTED_MANAGERS', JSON.stringify(this.__selectedManagers));
      this.requestUpdate('__selectedManagers');
      this.updateComplete.then(() => {
        const removeButtons = this.renderRoot.querySelectorAll<HTMLElement>('.person-row__action');
        const next = removeButtons[Math.min(idx, removeButtons.length - 1)];
        if (next) next.focus();
        else this.__focusSearchInput();
      });
    } catch (err: unknown) {
      this.showError(err);
      this.__focusAfterListChange();
    }
  }

  private __setPrimaryManager(id: string) {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    this.clearError();

    try {
      const manager = this.__selectedManagers.find((sm) => sm.id === id);
      if (!manager) throw new Error(`Could not find a manager with ID: ${id} in the selected managers list`);

      this.__selectedManagers.forEach((sm) => {
        sm.primary = sm.id === id;
      });

      this.__announce(`${manager.name} set as your primary manager.`);

      this.updateMetadata('SELECTED_MANAGERS', JSON.stringify(this.__selectedManagers));
      this.requestUpdate('__selectedManagers');
    } catch (err: unknown) {
      this.showError(err);
    }
  }

  private __onDoesNotReportToAnyoneChange(checked: boolean) {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    this.clearError();

    this.__doesNotReportToAnyone = checked;
    if (checked) this.__pendingSelected = [];

    this.__announce(
      checked
        ? 'Marked as not reporting to anyone within this company. The search below is now disabled.'
        : 'You can now search for and select your managers below.',
    );

    this.updateMetadata('DOES_NOT_REPORT_TO_ANYONE', checked ? 'true' : 'false');
  }

  private __renderSearch() {
    return html`
      <div class="vertical search-field">
        <label class="visually-hidden" id="managers-search-label" for="managers-search">Search for your managers</label>
        <div class="horizontal center-center search-row">
          <div class="flex search-input-wrapper" aria-disabled=${this.__doesNotReportToAnyone ? 'true' : 'false'}>
            <ns-multi-select
              no-auto-fill
              id="managers-search"
              page-size="10"
              label="Search for your managers"
              placeholder="Search by name..."
              .options=${this.__searchResults}
              .selected=${this.__pendingSelected}
              .searchStatus=${this.__searchStatus}
              @search-input=${(evt: CustomEvent<{ query: string }>) => this.__onSearchInput(evt)}
              @selection-changed=${(evt: CustomEvent<{ selected: Option[] }>) => this.__onSelectionChanged(evt)}
            ></ns-multi-select>
          </div>

          <wa-button
            variant="brand"
            size="small"
            class="select-button ml-2"
            ?disabled=${this.__doesNotReportToAnyone || this.__pendingSelected.length === 0}
            @click=${() => this.__onSelect()}
          >
            Select
            <span class="visually-hidden">the chosen people to add them to your managers list</span>
          </wa-button>
        </div>
      </div>
    `;
  }

  private __renderManagersList() {
    if (this.__doesNotReportToAnyone) {
      return html`
        <div id="managers-list" class="managers-list vertical center-center" role="region" aria-label="Your managers">
          <div class="empty-state">
            <div class="empty-state__title">Nothing to select</div>
            <div class="empty-state__body">
              You've told us you don't report to anyone within this company, so there's no need to choose a manager
              here. Untick the box above if that's not right.
            </div>
          </div>
        </div>
      `;
    }

    if (this.__selectedManagers.length === 0) {
      return html`
        <div id="managers-list" class="managers-list vertical center-center" role="region" aria-label="Your managers">
          <div class="empty-state">
            <div class="empty-state__title">Your managers will appear here</div>
            <div class="empty-state__body">
              Search for people above and click
              <strong>Select</strong>
              to add them to this list. Once you've added everyone you report to, choose exactly one of them as your
              primary manager.
            </div>
          </div>
        </div>
      `;
    }

    return html`
      <div
        id="managers-list"
        class="managers-list"
        role="radiogroup"
        aria-label="Your selected managers - choose one as your primary manager"
      >
        ${repeat(
          this.__selectedManagers,
          (manager) => manager.id,
          (manager) => html`
            <div class="person-row">
              <label class="person-row__primary" for="primary-${manager.id}">
                <input
                  type="radio"
                  id="primary-${manager.id}"
                  name="primary-manager"
                  aria-label="Set ${manager.name} as your primary manager"
                  .checked=${manager.primary}
                  @change=${() => this.__setPrimaryManager(manager.id)}
                />

                <div class="person-row__avatar horizontal center-center">
                  ${manager.avatar && !this.__brokenAvatars.has(`${manager.id}:${manager.avatar}`)
                    ? html`
                        <img
                          src="${manager.avatar}"
                          alt=""
                          @error=${() => this.__onAvatarError(`${manager.id}:${manager.avatar}`)}
                        />
                      `
                    : this.__getInitials(manager.name)}
                </div>

                <div class="person-row__cell">
                  <span class="value">${manager.name}</span>
                  <span class="caption">
                    <span class="caption-text">${manager.role ?? ''}</span>
                    ${manager.primary
                      ? html`
                          <span class="status-badge">Primary</span>
                        `
                      : nothing}
                  </span>
                </div>

                <div class="person-row__cell">
                  <span class="value">${manager.email}</span>
                  <span class="caption">Email</span>
                </div>

                <div class="person-row__cell">
                  <span class="value">${manager.role ?? ''}</span>
                  <span class="caption">Job role</span>
                </div>
              </label>

              <button
                type="button"
                class="person-row__action"
                aria-label="Remove ${manager.name} from your managers list"
                @click=${(e: MouseEvent) => {
                  if (e.detail > 1) return;
                  this.__deselectManager(manager.id);
                }}
              >
                <ns-icon icon="times"></ns-icon>
              </button>
            </div>
          `,
        )}
      </div>
    `;
  }

  render() {
    return html`
      <ns-job-layout image="/images/jobs/reporting-to.png" image-label="Reporting structure illustration">
        ${this.__announcer.render()}
        <div class="job-panel">
          <wa-checkbox
            .checked=${this.__doesNotReportToAnyone}
            @change=${(e: Event) => this.__onDoesNotReportToAnyoneChange((e.target as WaCheckbox).checked)}
          >
            I do not report to anyone at
            <strong class="company-name">${this.__companyName}</strong>
          </wa-checkbox>
        </div>
        <div class="job-panel">
          <p>
            Please select from the below list of managers for
            <strong class="company-name">${this.__companyName}</strong>
          </p>
          ${this.__renderSearch()} ${this.__renderManagersList()}
        </div>
      </ns-job-layout>
    `;
  }
}
